import { JSX, useCallback, useEffect, useRef, useState } from 'react'

import { components, SingleValueProps } from 'react-select'
import { omit } from 'lodash'
import {
  TreeSelect,
  TreeSelectOption,
  TreeSelectPathSegment
} from '~/design_system/components/form_controls/TreeSelect'
import { useBusinessUnitConfigQuery, BusinessUnit, useBusinessUnitsQueryForPicker } from '~/api/businessUnits'
import { forceArray } from '~/common/arrayUtils'
import asJqueryPlugin from '~/common/jqueryUtils'
import { useMsg } from '~/common/localizationUtils'
import { asFormikField } from '~/common/formikUtils'
import { useDebouncedValue } from '~/hooks/useDebouncedValue'
import { useLocalStorage } from '~/hooks/useBrowserStorage'
import api from '~/settings/business-unit/api'
import { Stack, Text, Tooltip } from '~/design_system'
import colors from '~/theme/colors'
import { useIntl } from '~/components/IntlProviders'
import createDebugLogger from '~/common/logger'

const debug = createDebugLogger('VndlyBusinessUnitPickerV2')

export interface VndlyBusinessUnitPickerv2Props {
  id?: string
  name?: string
  value?: number | number[]
  onChange?: (event: any) => void
  minLevel?: number
  lockParent?: number
  placeholderText?: string | JSX.Element
  overrideClientName?: string
  overrideRootName?: string
  readonly?: boolean
  isMulti?: boolean
  userRestrictedParent?: boolean
  wordWrap?: boolean
  permission?: string
  shouldCallOnChangeOnInitialLoad?: boolean
}

const MIN_LEVEL_VISIBLE = 2

const CustomFilter = (candidate: any, input: any) => {
  const { label, data } = candidate
  const optionCode = data.code?.toLowerCase() ?? ''
  const optionLabel = label?.toLowerCase() ?? ''

  if (input) {
    const lowerInput = input.toLowerCase()
    return optionCode.includes(lowerInput) || optionLabel.includes(lowerInput)
  }

  return true
}

export function VndlyBusinessUnitPickerv2({
  id = 'bu_picker',
  name = id,
  value,
  onChange = () => {},
  minLevel = MIN_LEVEL_VISIBLE,
  lockParent,
  placeholderText,
  overrideClientName = 'All',
  overrideRootName = 'Root',
  readonly = false,
  isMulti = false,
  userRestrictedParent = false,
  permission,
  shouldCallOnChangeOnInitialLoad = false,
  ref: _ref,
  ...props
}: VndlyBusinessUnitPickerv2Props & { ref?: React.Ref<HTMLInputElement> }) {
  const msg = useMsg()
  const { formatList } = useIntl()
  const defaultPlaceholderText = msg('vndly_bu_picker.placeholder.select_an_organization_unit')
  const resolvedPlaceholderText = placeholderText || defaultPlaceholderText
  const [currentParent, setCurrentParent] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300)
  const [selectedValues, setSelectedValues] = useState<TreeSelectOption[]>([])
  const [recentlyUsed, setRecentlyUsed] = useLocalStorage(`${id}-recently-used`, []) as [
    TreeSelectOption[],
    (value: TreeSelectOption[]) => void
  ]
  const initialOnChangeRendered = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data: displayConfig, isLoading: isDisplayConfigLoading } = useBusinessUnitConfigQuery()
  const showSubLabelOnEdit = displayConfig?.show_sub_label_on_edit && !readonly
  const showSubLabelOnView = displayConfig?.show_sub_label_on_view

  const transformBusinessUnitToTreeSelectOption = (bu: BusinessUnit): TreeSelectOption => {
    let label
    if (bu.level === 1) {
      label = overrideClientName
    } else if (bu.level === 0) {
      label = overrideRootName
    } else {
      label = bu.name
    }

    // EITHER edit or view behavior relies on the option's secondLine to contain the sub label
    // If the setting is only for edit, the custom SingleValue render will hide it
    const secondLine = (showSubLabelOnEdit || showSubLabelOnView) && bu.sub_label ? bu.sub_label : undefined

    // if you search we show a third line with the hierarchy for ambiguious results
    const thirdLine = searchTerm ? bu.path_segments?.map(a => a.label).join(' > ') : undefined

    const buLevel = bu.level || 0

    return {
      ...bu,
      value: bu.id,
      label,
      isDisabled: !bu.has_access || minLevel > buLevel,
      hasChildren: bu.children && bu.children.length > 0,
      path: bu.path_segments || [],
      secondLine,
      thirdLine: thirdLine === secondLine ? undefined : thirdLine // Only show if it's not a duplicate
    }
  }

  const lowestLevelVisible = Math.min(minLevel, MIN_LEVEL_VISIBLE)

  const { data: businessUnitsResponse, isLoading: businessUnitsQueryIsLoading } = useBusinessUnitsQueryForPicker({
    parent: currentParent,
    minLevel: lowestLevelVisible,
    permission,
    search: debouncedSearchTerm || undefined,
    lockParent: lockParent ? { lock: lockParent, parents: [] } : undefined,
    userRestrictedParent,
    include_path_segments: true
  })

  const businessUnits = businessUnitsResponse?.results || []

  const emitChangeEvent = (valueArray: BusinessUnit[]) => {
    let event
    // The shape of the event is a little inconsistent but I kept it for backwards compatibility with the old component
    if (isMulti) {
      event = { value: valueArray, target: inputRef.current }
    } else {
      event = { ...valueArray[0], target: inputRef.current }
    }
    debug.info('Emitting onChange:', event)
    onChange(event)
  }
  const smartSelectParent = (valueArray: TreeSelectOption[]) => {
    // search could have taken us to a completely different tree from our active nav,
    // so re-set parent on any selection change
    // Also reverse values for multiselect - parent should persist based on last selection not first
    const reversedValues = [...valueArray].reverse()
    debug('smartSelectParent', reversedValues)
    if (reversedValues.length) {
      setCurrentParent([...reversedValues[0].path].reverse()[0]?.value as number)
    }
  }

  const cleanInitialValues = forceArray(value).filter(Boolean)
  debug('Initial value:', value, cleanInitialValues.length)
  useEffect(() => {
    if (value && !isDisplayConfigLoading) {
      // Fetch the full business unit details for the initial values coming from url params
      const valueArray = cleanInitialValues
      if (valueArray.length > 0) {
        Promise.all(valueArray.map(id => api.businessUnit.get(id))).then((businessUnits: BusinessUnit[]) => {
          const opts = businessUnits.map(transformBusinessUnitToTreeSelectOption)
          debug('setSelectedValues', opts)
          /*
              eslint-disable-next-line react-hooks/set-state-in-effect --

              ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
              Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
              There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
              couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

              Issue: Setting selectedValues from initial values in effect causes cascading renders.
              Possible fix approach: Initialize selectedValues from initial values in useState:
                const [selectedValues, setSelectedValues] = useState(() => computeInitialSelectedValues(cleanInitialValues))
            */
          setSelectedValues(opts)
          smartSelectParent(opts)
          // emit change on init, some things need to receive WD staffing models etc
          emitChangeEvent(businessUnits)
        })
      } else {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Clearing selection when no initial values
        setSelectedValues([])
      }
    }
  }, [cleanInitialValues.length, isDisplayConfigLoading])

  const options = businessUnits.map(transformBusinessUnitToTreeSelectOption)

  useEffect(() => {
    if (
      shouldCallOnChangeOnInitialLoad &&
      !isMulti &&
      !value &&
      !businessUnitsQueryIsLoading &&
      options.length > 0 &&
      !initialOnChangeRendered.current
    ) {
      // If there is no initial value, auto-select the first option and emit onChange when shouldCallOnChangeOnInitialLoad is true
      initialOnChangeRendered.current = true
      const selectedBU = options[0]
      /*
          eslint-disable-next-line react-hooks/set-state-in-effect --

          ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
          Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
          There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
          couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

          Issue: Auto-selecting first option in effect causes cascading render.
          Possible fix approach: Handle this in useState initialization or lift the auto-select logic
          to the parent component so initial state is set before first render.
        */
      setSelectedValues([selectedBU])
      emitChangeEvent([selectedBU as unknown as BusinessUnit])
    }
  }, [options.length, isMulti, value, businessUnitsQueryIsLoading, shouldCallOnChangeOnInitialLoad])

  debug('Options state:', { businessUnitsResponse, options, minLevel, currentParent })
  const handleTraverseTree = useCallback((newNode: TreeSelectPathSegment | null) => {
    if (newNode === null) {
      // Navigate to root
      setCurrentParent(null)
    } else {
      setCurrentParent(newNode.value as number)
    }
    setSearchTerm('')
  }, [])

  const handleSelectionChange = (newValue: TreeSelectOption | TreeSelectOption[]) => {
    const valueArray = Array.isArray(newValue) ? newValue : [newValue]

    setSelectedValues(valueArray)
    smartSelectParent(valueArray)

    // Update recently used in local storage
    const newRecentlyUsed = [...recentlyUsed]
    for (const item of valueArray) {
      if (!newRecentlyUsed.find(i => i.value === item.value)) {
        newRecentlyUsed.push(item)
      }
    }
    setRecentlyUsed(newRecentlyUsed.slice(-3))

    // do a detail get on the selected option(s) and emit those as changes.  Need the extra fields
    Promise.all(valueArray.map(opt => api.businessUnit.get(opt.value))).then((businessUnits: BusinessUnit[]) => {
      emitChangeEvent(businessUnits)
    })
  }

  if (readonly) {
    const currentValue = Array.isArray(selectedValues) ? selectedValues[0] : selectedValues || null
    const displayValue = isMulti
      ? formatList(
          selectedValues.map(v => v.label),
          { type: 'unit' }
        )
      : selectedValues[0]?.label || ''
    const selectedSubLabel = isMulti ? undefined : selectedValues[0]?.secondLine

    debug('readonly props', { selectedValues, currentValue, selectedSubLabel, displayValue })
    return (
      <Stack spacing={0} data-testid={`bu-picker-${isMulti ? 'multiple' : 'single'}-select-readonly`} data-readonly>
        <Text>{displayValue}</Text>
        {showSubLabelOnView && selectedSubLabel && (
          <Tooltip label={selectedSubLabel} placement="bottom">
            <div
              // Define text color in JS so we can use design system tokens
              style={{ color: colors.gray[500] }}>
              {selectedSubLabel}
            </div>
          </Tooltip>
        )}
      </Stack>
    )
  }

  return (
    <TreeSelect
      id={id}
      name={name}
      options={options}
      onChange={handleSelectionChange}
      onSearch={setSearchTerm}
      searchValue={searchTerm}
      onTraverseTree={handleTraverseTree}
      isMulti={isMulti}
      placeholder={resolvedPlaceholderText}
      isDisabled={readonly}
      isLoading={businessUnitsQueryIsLoading}
      // Omit search path from recentlyUsed
      recentlyUsed={recentlyUsed.map(ru => omit(ru, 'thirdLine'))}
      value={selectedValues}
      isClearable={false}
      data-testid={`bu-picker-${isMulti ? 'multiple' : 'single'}-select`}
      customReactSelectComponents={showSubLabelOnView ? { SingleValue: SingleValueWithSubLabel as any } : undefined}
      customFilter={CustomFilter}
      {...props}
    />
  )
}

function SingleValueWithSubLabel({ children, ...props }: SingleValueProps<BusinessUnit>) {
  return (
    <components.SingleValue {...props}>
      <Stack gap={0}>
        <Text textStyle="body" m={0}>
          {children}
        </Text>
        {props.data?.sub_label && (
          <Text textStyle="subtextSmall" m={0}>
            {props.data?.sub_label}
          </Text>
        )}
      </Stack>
    </components.SingleValue>
  )
}

VndlyBusinessUnitPickerv2.displayName = 'VndlyBusinessUnitPickerv2'

export const FormikVndlyBusinessUnitPickerv2 = asFormikField(
  VndlyBusinessUnitPickerv2,
  ({ form, field, id, readOnly }: any) => ({
    name: field.name || id,
    id,
    value: field.value,
    readonly: readOnly,
    onChange: (bu_obj: any) => {
      if (Array.isArray(bu_obj)) {
        // Multi-select: use array of IDs as numbers
        const ids = bu_obj.map((bu: any) => bu.id)
        form.setFieldValue(field.name, ids)
      } else {
        // Single select: use business unit ID as number
        form.setFieldValue(field.name, bu_obj?.id)
      }
    }
  })
)

asJqueryPlugin('VndlyBusinessUnitPickerv2', VndlyBusinessUnitPickerv2)
